/**
 * DSP helpers for the SFX recipes (audio-direction §7): seeded noise and click buffers, waveshaper curves,
 * envelopes and sweeps on `AudioParam`s. Everything is deterministic for a given `Rng` and runs at init
 * (pre-render) time only, so allocating buffers and curves here is fine.
 */
import { createRng, nextFloat, type Rng } from '../../core/rng'

/** −60 dB in nepers: an exponential decay reaches −60 dB after `T60 = 6.91 τ`. */
export const T60_TAU = Math.log(1000)

/** A tiny floor for exponential ramps (they cannot reach 0). */
export const SILENT = 1e-5

export type NoiseColor = 'white' | 'pink' | 'brown'

/** Uniform in [-1, 1). */
export function bipolar(rng: Rng): number {
  return nextFloat(rng) * 2 - 1
}

/** `value × (1 ± spread)`, uniformly. */
export function jitter(rng: Rng, value: number, spread: number): number {
  return spread > 0 ? value * (1 + bipolar(rng) * spread) : value
}

/** Fills `out` with seeded noise of `color`, peak-normalised to about ±1. */
export function fillNoise(out: Float32Array, rng: Rng, color: NoiseColor): void {
  if (color === 'white') {
    for (let i = 0; i < out.length; i++) out[i] = bipolar(rng)
    return
  }
  if (color === 'pink') {
    // Paul Kellet's economy pink filter (−3 dB/octave within ±0.5 dB above ~40 Hz).
    let b0 = 0
    let b1 = 0
    let b2 = 0
    for (let i = 0; i < out.length; i++) {
      const w = bipolar(rng)
      b0 = 0.99765 * b0 + w * 0.099046
      b1 = 0.963 * b1 + w * 0.2965164
      b2 = 0.57 * b2 + w * 1.0526913
      out[i] = (b0 + b1 + b2 + w * 0.1848) * 0.2
    }
    return
  }
  // Brown: leaky integration of white noise (−6 dB/octave), DC-free thanks to the leak.
  let y = 0
  for (let i = 0; i < out.length; i++) {
    y = 0.996 * y + bipolar(rng) * 0.06
    out[i] = y * 1.6
  }
}

/** Seconds of each shared noise buffer; longer layers loop it. */
export const NOISE_SECONDS = 4
const NOISE_SEED = 0x6e015e
const COLORS: readonly NoiseColor[] = ['white', 'pink', 'brown']
/**
 * Noise is generated once per (colour, channels, rate) and shared by every render (AudioBuffers are not tied to
 * a context): generating it per layer per variation was most of the pre-render's main-thread time.
 */
const noiseCache = new Map<string, AudioBuffer>()

export interface NoiseTake {
  readonly src: AudioBufferSourceNode
  /** Where in the shared buffer to start (seconds): the seeded part of the take. */
  readonly offset: number
}

/** A source of seeded noise (mono, or decorrelated stereo): a random window of the shared buffer. Not started. */
export function noiseSource(ac: BaseAudioContext, rng: Rng, seconds: number, color: NoiseColor, channels: 1 | 2 = 1): NoiseTake {
  const rate = ac.sampleRate
  const key = `${color}:${channels}:${rate}`
  let buffer = noiseCache.get(key)
  if (buffer === undefined) {
    buffer = ac.createBuffer(channels, Math.ceil(NOISE_SECONDS * rate), rate)
    for (let c = 0; c < channels; c++) fillNoise(buffer.getChannelData(c), createRng(NOISE_SEED + COLORS.indexOf(color) * 7 + c * 101), color)
    noiseCache.set(key, buffer)
  }
  const src = ac.createBufferSource()
  src.buffer = buffer
  const room = NOISE_SECONDS - seconds
  if (room <= 0) src.loop = true
  return { src, offset: nextFloat(rng) * (room > 0 ? room : NOISE_SECONDS) }
}

/** Symmetric tanh saturation curve; `drive` 0 is linear. Output peak is 1 at input ±1. */
export function tanhCurve(drive: number, points = 1024): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(points)
  const k = Math.max(drive, 1e-3)
  const norm = Math.tanh(k)
  for (let i = 0; i < points; i++) {
    const x = (i / (points - 1)) * 2 - 1
    curve[i] = Math.tanh(k * x) / norm
  }
  return curve
}

/** A waveshaper with a tanh curve and 2× oversampling (keeps the grit, drops the aliasing). */
export function saturator(ac: BaseAudioContext, drive: number): WaveShaperNode {
  const s = ac.createWaveShaper()
  s.curve = tanhCurve(drive)
  s.oversample = '2x'
  return s
}

/**
 * Attack–hold–decay on `param` from `t0`: 0 → `peak` linearly over `attack`, holds, then decays exponentially,
 * reaching −60 dB after `decay` seconds. Returns the time the envelope is effectively silent.
 */
export function ahd(param: AudioParam, t0: number, attack: number, hold: number, decay: number, peak: number): number {
  param.setValueAtTime(0, t0)
  const top = t0 + Math.max(attack, 1e-4)
  param.linearRampToValueAtTime(peak, top)
  if (hold > 0) param.setValueAtTime(peak, top + hold)
  param.setTargetAtTime(0, top + hold, Math.max(decay, 1e-4) / T60_TAU)
  return top + hold + decay * 1.15
}

/** Exponential sweep of `param` from `from` to `to` over `seconds` (both > 0). */
export function sweep(param: AudioParam, t0: number, from: number, to: number, seconds: number): void {
  param.setValueAtTime(from, t0)
  if (seconds > 0 && to !== from) param.exponentialRampToValueAtTime(Math.max(to, SILENT), t0 + seconds)
}

/** A smooth random walk (1 ± depth) of `points` values for `setValueCurveAtTime`, scaled by `base`. */
export function wanderCurve(rng: Rng, points: number, base: number, depth: number, smooth: number): Float32Array<ArrayBuffer> {
  const out = new Float32Array(Math.max(2, points))
  let v = 0
  let target = 0
  for (let i = 0; i < out.length; i++) {
    if (i % 4 === 0) target = bipolar(rng)
    v += (target - v) * (1 - smooth)
    out[i] = base * (1 + v * depth)
  }
  return out
}

export interface ClickSpec {
  /** Number of clicks. */
  count: number
  /** First click time (s) and the span the clicks spread over. */
  start: number
  span: number
  /** > 1 packs clicks towards the start (debris thins out), 1 is uniform. */
  thinning: number
  /** Each click is a noise burst decaying to −60 dB in this many seconds. */
  clickDecay: number
  /** How much a click's level drops across the span (0 = none, 1 = to silence). */
  fade: number
}

/**
 * A buffer of sparse seeded clicks (rattles, crackle, debris). In stereo each click gets its own equal-power
 * position within ±`spread`. Returns an unstarted source.
 */
export function clickSource(ac: BaseAudioContext, rng: Rng, spec: ClickSpec, channels: 1 | 2, spread: number): AudioBufferSourceNode {
  const rate = ac.sampleRate
  const seconds = spec.start + spec.span + spec.clickDecay * 1.2
  const length = Math.max(1, Math.ceil(seconds * rate))
  const buffer = ac.createBuffer(channels, length, rate)
  const left = buffer.getChannelData(0)
  const right = channels === 2 ? buffer.getChannelData(1) : left
  const clickLen = Math.max(1, Math.ceil(spec.clickDecay * 1.2 * rate))
  const k = T60_TAU / Math.max(spec.clickDecay * rate, 1)
  for (let c = 0; c < spec.count; c++) {
    const u = Math.pow(nextFloat(rng), spec.thinning)
    const t = spec.start + u * spec.span
    const level = (0.45 + 0.55 * nextFloat(rng)) * (1 - spec.fade * u)
    const p = channels === 2 ? bipolar(rng) * spread : 0
    const angle = ((p + 1) * Math.PI) / 4
    const gl = channels === 2 ? Math.cos(angle) : 1
    const gr = channels === 2 ? Math.sin(angle) : 0
    const first = Math.floor(t * rate)
    for (let i = 0; i < clickLen && first + i < length; i++) {
      const s = bipolar(rng) * level * Math.exp(-k * i)
      left[first + i] += s * gl
      if (channels === 2) right[first + i] += s * gr
    }
  }
  const src = ac.createBufferSource()
  src.buffer = buffer
  return src
}

/** Peak absolute sample of a buffer (all channels). */
export function bufferPeak(buffer: AudioBuffer): number {
  let peak = 0
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const d = buffer.getChannelData(c)
    for (let i = 0; i < d.length; i++) {
      const a = Math.abs(d[i])
      if (a > peak) peak = a
    }
  }
  return peak
}

export function dbToGain(db: number): number {
  return Math.pow(10, db / 20)
}

export function gainToDb(g: number): number {
  return 20 * Math.log10(Math.max(g, 1e-12))
}
